import { useState } from "react";
import { addDomain, removeDomain } from "../data/api";
import type { Account } from "../data/types";
import { TextInput } from "./Field";
import { Icon } from "./Icon";

/**
 * The account's login domains for the customer portal.
 *
 * Anyone with an email on one of these domains can sign in and see this
 * account, so adding a domain IS granting access. The list therefore starts
 * empty rather than inheriting emailDomain — that value is only offered as
 * the first suggestion. Admin-only in the UI; the routes refuse other roles
 * too (hiding is decoration, §3.4).
 */
export function DomainsEditor({ account, onChanged }: { account: Account; onChanged: () => void }) {
  const [draft, setDraft] = useState(account.domains.length === 0 ? account.emailDomain : "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
      setDraft("");
    } catch (e) {
      // send() throws a Response whose text is the server's message
      // ("already used by SpaceX", "public email provider"…) — show that.
      setError(e instanceof Response ? await e.text() : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  const trimmed = draft.trim();

  return (
    <div>
      <div className="mb-1 text-xs font-medium uppercase tracking-wide text-[var(--text-3)]">Login domains</div>
      <p className="mb-2 text-xs text-[var(--text-2)]">
        Anyone with an email on these domains can sign in to the customer portal and see this account.
      </p>

      {account.domains.length === 0 ? (
        <p className="mb-2 text-sm text-[var(--text-2)]">No portal access yet.</p>
      ) : (
        <ul className="mb-2 flex flex-wrap gap-1.5">
          {account.domains.map((d) => (
            <li key={d} className="flex items-center gap-1 rounded-full bg-gray-100 py-0.5 pl-2.5 pr-1 text-xs font-medium text-gray-700">
              {d}
              <button
                type="button"
                aria-label={`Remove domain ${d}`}
                disabled={busy}
                onClick={() => void run(() => removeDomain(account.id, d))}
                className="rounded-full p-0.5 text-[var(--text-2)] hover:text-error-600 focus-visible:focus-ring disabled:opacity-50"
              >
                <Icon name="x" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (trimmed) void run(() => addDomain(account.id, trimmed));
        }}
      >
        <TextInput aria-label="Login domain" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="acme.com" />
        <button
          type="submit"
          disabled={busy || trimmed === ""}
          className="flex-none rounded-md bg-brand-600 px-2 py-1 text-xs font-semibold text-white hover:bg-brand-700 focus-visible:focus-ring disabled:opacity-50"
        >
          Add domain
        </button>
      </form>
      {error && (
        <p role="alert" className="mt-1 text-xs text-error-600">
          {error}
        </p>
      )}
    </div>
  );
}
